/**
 * S14. Keys and settings.
 *
 * Purpose, in one line: hold secrets safely, and be completely honest about
 * what the platform can and cannot see.
 *
 * THE WRITE ONLY NOTICE IS NOT A FOOTNOTE. It sits above the list, it is never
 * dismissible, and it says the consequence before a person can hit it: once a
 * key is saved we cannot show it back, so a lost key is replaced rather than
 * recovered. That is a product decision about zero knowledge secrets, and the
 * only place it means anything is next to the field that creates one.
 *
 * THE RESERVED ROW IS RENDERED FIRST, and it says why rather than being greyed
 * out. A row that is inert with no explanation is a row a person pokes at
 * until they decide the product is broken. DATABASE_URL exists because the
 * platform connected the app to its own database, and the row says exactly
 * that, in the vocabulary the rest of the product uses for a connection.
 *
 * THE DUPLICATE NAME PATH DOES NOT BLOCK. Spec S14: a name that already exists
 * is explained and allowed, because replacing a key is the single most common
 * legitimate reason to open this screen, and a validation error would make the
 * product argue with a person who is doing the right thing. The consequence
 * goes in the field's help line rather than its error line on purpose: the
 * error line sets aria-invalid, and telling a screen reader that a perfectly
 * good key name is invalid would be a lie. The primary relabels to "Replace
 * key", which is the real signal.
 *
 * DEVIATION, recorded. Spec S14 zone 3 puts Replace and Delete behind a kebab.
 * There is no menu primitive in the M1 kit and inventing one here would land a
 * second popover pattern in the product without a review. The two actions sit
 * in the row as bare labels instead: at four rows it is quieter than a kebab,
 * it is one tab stop closer for a keyboard, and the day the kit grows a menu
 * this row is where it goes.
 *
 * SIMPLE AND ADVANCED. Two things append and neither one is a mode check: the
 * deploy that last picked a key up, inside the row, and the readable settings,
 * in a disclosure at the foot of the page. Both are one click away in Simple.
 * The section is called Settings, not "Non-secret settings": the page already
 * carries the distinction in its own name and in the note under the heading,
 * and even Advanced keeps the product's words for the product's things.
 *
 * FAKEABLE STATES, deep linkable per prototype mechanic (a):
 *   ?state=empty        no keys yet, with the add form as the empty state's action
 *   ?state=changed      the redeploy strip, as it appears after a change
 *   ?state=duplicate    the add form pre filled with a name that already exists
 *   ?state=loading      the skeletons, held open
 *
 * // reads through environmentOf(); do not flatten.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../../components/primitives/Button';
import { Field } from '../../components/primitives/Field';

import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { NarratedLine } from '../../components/patterns/AsyncNarration';
// whenLabel is the "relative under seven days, absolute beyond it" rule from
// spec 6.5 rule 8. It lives on the deploy row because that is where it landed
// first, and a key's added date follows the same rule, so it is imported
// rather than written a second time.
import { whenLabelOrNull } from '../../components/patterns/DeployRow';
import { DestructiveConfirm } from '../../components/patterns/DestructiveConfirm';
import { EmptyState } from '../../components/patterns/EmptyState';
import { Notice } from '../../components/patterns/Notice';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { Skeleton } from '../../components/patterns/Skeleton';

import { environmentOf, getAccount, getProject, listDeploys, listSecrets } from '../../lib/api';
import { useAccount, useProjects } from '../../lib/auth/use-app-data';
import {
  clearKeysChanged,
  keysAwaitingDeploy,
  markKeysChanged,
  readKeysChangedAt,
} from '../../lib/secrets-reminder';
import { deleteSecret, setSecret } from '../../lib/real-api';
import { showToast } from '../../components/primitives/Toast';
import { shortSha } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { useTicker } from '../../lib/hooks/use-ticker';
import { t } from '../../lib/i18n/t';
import { stateOf } from '../../lib/mock/simulate';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import { useFormTracker } from '../../lib/analytics/use-form-tracker';
import type { Deploy, Secret, Setting } from '../../lib/types';

import '../../styles/components/keys.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

const STATES = ['empty', 'changed', 'duplicate', 'loading', 'default'] as const;
type StateName = (typeof STATES)[number];

/** Twelve dots, always, whatever the value was. A mask that reported length
 *  would be reporting something about the secret. */
const MASK = '•'.repeat(12);

/**
 * The common keys the helper chips offer, and where each one comes from.
 *
 * This is product content rather than demo data, so it lives with the screen:
 * the fixture describes one account's projects, and this list would be the
 * same on an empty one.
 */
interface CommonKey {
  chip: string;
  name: string;
  where: string;
  href: string;
}

/**
 * Built per render rather than declared as a constant, and the reason is the
 * catalogue. Every chip and every sentence here is painted, so every one has
 * to be a literal inside t(): a list of bare English strings handed to
 * t(variable) at the render site is invisible to a literal scanning extractor,
 * so it renders in English in every language forever. Rebuilding it per render
 * also means the list follows a language change like everything else.
 *
 * The variable names are the exact ones each service documents, because a
 * helper that fills in a name a person then has to correct is worse than no
 * helper. The chips are the product's own words: the chip beside STRIPE_SECRET_KEY
 * reads "Stripe key", because the chip is painted in Simple and Simple has no
 * business teaching somebody the difference between one kind of key and another.
 */
function commonKeys(): CommonKey[] {
  return [
    {
      chip: t('OpenAI key'),
      name: 'OPENAI_API_KEY',
      where: t('Sign in to OpenAI, open your account, then API keys.'),
      href: 'https://platform.openai.com/api-keys',
    },
    {
      chip: t('Stripe key'),
      name: 'STRIPE_SECRET_KEY',
      where: t('Sign in to Stripe, open Developers, then API keys.'),
      href: 'https://dashboard.stripe.com/apikeys',
    },
    {
      chip: t('Resend key'),
      name: 'RESEND_API_KEY',
      where: t('Sign in to Resend, then open API keys.'),
      href: 'https://resend.com/api-keys',
    },
  ];
}

/**
 * Replace moves focus into the form, which is a real focus move rather than a
 * scroll: a keyboard user pressing Replace has to land on the field they are
 * about to type in. The Field primitive does not forward a ref, and giving it
 * one for a single caller would change a kit component from a screen, so this
 * reaches for the id the field already has.
 */
const NAME_FIELD_ID = 'key-name';

function focusKeyName(): void {
  document.getElementById(NAME_FIELD_ID)?.focus();
}

/* --------------------------------------------------------------- the rows */

function LockGlyph() {
  return (
    <span className="keyrow__glyph" aria-label={t('Locked')} role="img">
      <svg viewBox="0 0 16 16" width="13" height="13" fill="none" focusable="false">
        <rect
          x="3.2"
          y="7"
          width="9.6"
          height="6.5"
          rx="1.2"
          stroke="currentColor"
          strokeWidth="1.3"
        />
        <path
          d="M5.6 7V5.2a2.4 2.4 0 0 1 4.8 0V7"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

/** Show/hide, drawn rather than imported: the kit has no icon set. */
function EyeGlyph({ shown }: { shown: boolean }) {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" focusable="false" aria-hidden="true">
      <path
        d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
      {shown ? (
        <path d="M2.5 13.5 13.5 2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      ) : null}
    </svg>
  );
}

function Mask() {
  return (
    <span className="keyrow__mask mono" role="img" aria-label={t('The value is hidden')}>
      {MASK}
    </span>
  );
}

interface KeyRowProps {
  secret: Secret;
  /** The deploy that last read this key. Advanced prints it, in mono. */
  pickedUpBy: Deploy | null;
  advanced: boolean;
  onReplace: () => void;
  onDelete: () => void;
}

function KeyRow({ secret, pickedUpBy, advanced, onReplace, onDelete }: KeyRowProps) {
  return (
    <li className="keyrow">
      <div className="keyrow__main">
        <div className="keyrow__top">
          <span className="keyrow__name mono">{secret.key}</span>
          <Mask />
        </div>
        <p className="keyrow__meta">
          {(() => {
            const when = whenLabelOrNull(secret.createdAt);
            if (secret.addedBy && when) return t('Added by {who}, {when}', { who: secret.addedBy, when });
            if (secret.addedBy) return t('Added by {who}', { who: secret.addedBy });
            if (when) return t('Added {when}', { when });
            return null;
          })()}
        </p>
        {/* The one mode check left on this screen, and it stays one on
            purpose: a disclosure trigger on every row would put four "Show
            technical details" buttons in a four row list, which is furniture
            competing with the two actions that matter. The line it hides is a
            single identifier a person can also read on the deploys timeline,
            so nothing is unreachable in Simple. */}
        {advanced && pickedUpBy ? (
          <p className="keyrow__meta">
            {t('Last picked up by deploy {sha}', { sha: shortSha(pickedUpBy.commitSha) })}
          </p>
        ) : null}
      </div>

      <div className="keyrow__actions">
        <Button variant="ghost" size="sm" onClick={onReplace}>
          {t('Replace')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete}>
          {t('Delete')}
        </Button>
      </div>
    </li>
  );
}

/**
 * The reserved row. It is visually distinct, it has no actions, and it carries
 * the sentence that explains itself. Rendered first, always, because it is the
 * connection the platform made and everything under it is one a person made.
 */
function ReservedRow({ secret }: { secret: Secret }) {
  return (
    <li className="keyrow keyrow--reserved">
      <div className="keyrow__main">
        <div className="keyrow__top">
          <LockGlyph />
          <span className="keyrow__name mono">{secret.key}</span>
          <Mask />
        </div>
        <p className="keyrow__meta">
          {t('Managed by SV Cloud. Your app is connected to your database with this.')}
        </p>
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ screen */

export default function Keys() {
  const slug = useProjectSlug() ?? '';
  const scenarioId = useDemoStore((s) => s.scenarioId);
  const { state } = useScreenState<StateName>(STATES, 'default');
  const [advanced] = useAdvanced();

  const cachedProjects = useProjects();
  const cachedProject = slug ? cachedProjects.find((p) => p.slug === slug) : undefined;
  const { data: fetchedProject, isLoading: isProjectLoading, error: projectError } = useAsync(
    () => (slug ? getProject(slug) : Promise.resolve(null)),
    [slug, scenarioId],
  );
  const project = fetchedProject ?? cachedProject;
  const projectId = project?.id ?? '';

  const { data: secretsData, error: secretsError } = useAsync(
    () => (projectId ? listSecrets(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  const { data: deploys = [] } = useAsync(
    () => (projectId ? listDeploys(projectId) : Promise.resolve([])),
    [projectId, scenarioId],
  );
  const cachedAccount = useAccount();
  const { data: fetchedAccount } = useAsync(getAccount, [scenarioId]);
  const account = fetchedAccount ?? cachedAccount;

  /* ---- what the person changed on this page ---- */

  const [added, setAdded] = useState<Secret[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);
  const [changedAt, setChangedAt] = useState<number | null>(null);

  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [pickedName, setPickedName] = useState<string | null>(null);
  // A key pasted from a password manager is unreadable at a glance and a key
  // typed by hand is unverifiable, so the field can be unmasked while it is
  // being filled in. It resets to masked on every submit and on every screen
  // change below: revealed is a momentary state, never a preference.
  const [valueShown, setValueShown] = useState(false);
  const [deleting, setDeleting] = useState<Secret | null>(null);

  useEffect(() => {
    setAdded([]);
    setRemoved([]);
    // Seeded from storage, not reset to null. The reminder is about the live
    // app's keys being older than this page's, which is still true after a
    // reload, so re-mounting this screen must not clear it.
    setChangedAt(
      state === 'changed' ? Date.now() : USE_MOCK ? null : readKeysChangedAt(projectId),
    );
    setName(state === 'duplicate' ? 'OPENAI_API_KEY' : '');
    setValue('');
    setValueShown(false);
    setPickedName(null);
  }, [slug, scenarioId, state, projectId]);

  /* ---- the one thing allowed to move ---- */

  const timelines = useDemoStore((s) => s.timelines);
  const startTimeline = useDemoStore((s) => s.startTimeline);
  const now = useTicker();

  const restartId = `keys-restart-${projectId}`;
  const line = timelines.find((one) => one.id === restartId) ?? null;
  const run = line ? stateOf(line, now) : null;

  const covered = line !== null && changedAt !== null && changedAt <= line.startedAt;
  const restarting = covered && run !== null && (run.status === 'running' || run.status === 'queued');
  const restarted = covered && run !== null && run.status === 'succeeded';

  // In a real build the demo timeline never exists, so what clears the
  // reminder is a real deploy that started after the change. Until one does,
  // the strip stays up on every visit to this page, which is the point: the
  // live app is running an older set of keys than the list below it.
  const deployStartedAts = useMemo(
    () => deploys.map((one) => new Date(one.createdAt).getTime()),
    [deploys],
  );
  const awaitingDeploy = keysAwaitingDeploy(changedAt, USE_MOCK ? undefined : deployStartedAts);
  const pending = USE_MOCK ? changedAt !== null && !covered : awaitingDeploy;

  // The stored marker is cleared by the same fact that hides the strip, so a
  // person who redeploys elsewhere (a push, `svcloud deploy`) does not come
  // back to a stale warning.
  useEffect(() => {
    if (USE_MOCK || !projectId || changedAt === null) return;
    if (!awaitingDeploy) {
      clearKeysChanged(projectId);
      setChangedAt(null);
      captureEvent('secrets_redeploy_reminder_cleared', { project_slug: slug });
    }
  }, [awaitingDeploy, changedAt, projectId, slug]);

  // One impression per visit, not per render. The number worth watching is
  // how long people carry this reminder before a deploy actually picks the
  // keys up: a large `age_hours` here is the failure the one-shot strip was
  // hiding.
  const reminderSeenRef = useRef(false);
  useEffect(() => {
    if (USE_MOCK || !awaitingDeploy || changedAt === null || reminderSeenRef.current) return;
    reminderSeenRef.current = true;
    captureEvent('secrets_redeploy_reminder_shown', {
      project_slug: slug,
      age_hours: Math.round(((Date.now() - changedAt) / 3_600_000) * 10) / 10,
    });
  }, [awaitingDeploy, changedAt, slug]);

  const secrets = useMemo(() => {
    const seeded = secretsData ?? [];
    const base = state === 'empty' ? seeded.filter((one) => one.reserved) : seeded;
    return [...base, ...added].filter((one) => !removed.includes(one.id));
  }, [secretsData, added, removed, state]);

  const reserved = secrets.filter((one) => one.reserved);
  const keys = secrets.filter((one) => !one.reserved);

  const formTracker = useFormTracker({
    formId: 'add-secret',
    formName: 'Add Secret Key',
    totalFields: 2,
  });

  const clash = secrets.find((one) => one.key === name.trim()) ?? null;
  const duplicate = clash && !clash.reserved ? clash : null;
  const takenByPlatform = clash?.reserved ? clash : null;

  useEffect(() => {
    if (duplicate) {
      captureEvent('secret_duplicate_warned', { key: duplicate.key });
    }
  }, [duplicate]);

  if ((isProjectLoading && !project) || secretsData === undefined || state === 'loading') {
    return (
      <div className="page">
        <Head />
        <Skeleton shape="panel" count={2} label={t('Loading your keys')} />
      </div>
    );
  }

  if (projectError || secretsError) {
    return (
      <div className="page">
        <Head />
        <EmptyState mark="key" sentence={t("We couldn't load your keys. Try refreshing the page.")} />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="page">
        <Head />
        <EmptyState mark="key" sentence={t('We cannot find that project.')} />
      </div>
    );
  }

  const settings: Setting[] = environmentOf(project).settings ?? [];
  const common = commonKeys();
  const picked = common.find((one) => one.name === pickedName) ?? null;

  function deployFor(secret: Secret): Deploy | null {
    if (!secret.lastDeployId) return null;
    return deploys.find((one) => one.id === secret.lastDeployId) ?? null;
  }

  function onAdd() {
    const key = name.trim();
    const secretValue = value;
    if (key.length === 0 || secretValue.length === 0 || takenByPlatform) return;

    formTracker.trackSubmit({ key_name: key, is_duplicate: Boolean(duplicate) });

    const at = Date.now();
    const record: Secret = {
      id: `sec_local_${key}_${at}`,
      projectId,
      key,
      // The value goes in and is never readable again, which is the whole
      // point of this screen. Nothing keeps it, not even the prototype.
      preview: '',
      reserved: false,
      createdAt: new Date(at).toISOString(),
      lastUsedAt: null,
      addedBy: account.githubHandle,
      // Nothing has picked it up yet, and it will not until the next deploy.
      // That is exactly what the strip below the header is telling them.
      lastDeployId: null,
    };

    setRemoved((was) => (duplicate ? [...was, duplicate.id] : was));
    setAdded((was) => [...was, record]);
    setChangedAt(at);
    if (!USE_MOCK) markKeysChanged(projectId, at);
    setName('');
    setValue('');
    setValueShown(false);
    setPickedName(null);
    formTracker.trackSubmitSuccess();

    if (!USE_MOCK) {
      setSecret(projectId, key, secretValue).catch((err: unknown) => {
        const errorMsg = err instanceof Error ? err.message : 'failed';
        formTracker.trackSubmitError('api_error', errorMsg);
        // The local row already reads as added, a write-only field can't be
        // un-typed, so a toast is the honest way to say it didn't take.
        showToast(t('{name} was not saved. Try adding it again.', { name: key }));
        setAdded((was) => was.filter((one) => one.id !== record.id));
      });
    }
  }

  function onRestart() {
    captureEvent('secrets_redeploy_now_clicked', { project_slug: slug });
    startTimeline({
      id: restartId,
      kind: 'redeploy',
      scriptId: 'redeploy',
      subjectId: projectId,
      startedAt: Date.now(),
      outcome: 'success',
    });
  }

  return (
    <div className="page">
      <Head />

      {/* Never dismissible. The consequence belongs where it lands. */}
      <Notice tone="quiet">
        <p>
          {t(
            "Once you save a key, we can't show it to you again. If you lose it, replace it with a new one.",
          )}
        </p>
      </Notice>

      {/* An inline strip, never a toast: a change that needs a restart is a
          fact about the page, and a message that leaves on its own is a
          message a person can miss. */}
      {/* Never dismissible, and it comes back on every visit until a deploy
          actually starts (lib/secrets-reminder.ts). It used to be a
          one-shot: a person who changed a key, walked away and came back saw
          nothing, and their app kept running the old value indefinitely with
          the console showing no sign of it. */}
      {pending ? (
        <div className="keys__strip">
          <p className="keys__strip-text">
            {USE_MOCK
              ? t('Your app will restart to pick this up. That takes about 30 seconds.')
              : t(
                  "Your app is still running your old keys. It picks these up on its next deploy.",
                )}
          </p>
          {USE_MOCK ? (
            <Button variant="ghost" onClick={onRestart}>
              {t('Redeploy now')}
            </Button>
          ) : (
            // No button, because there is no route that redeploys on demand:
            // a deploy comes from a push (or `svcloud deploy`, which pushes).
            // A button that pretended otherwise is what made the old strip
            // feel handled when nothing had happened.
            <span className="keys__strip-how mono">svcloud deploy {project.slug}</span>
          )}
        </div>
      ) : null}

      {restarting ? (
        <div className="keys__strip keys__strip--running">
          <NarratedLine sentence={t('Restarting your app')} />
        </div>
      ) : null}

      {restarted ? (
        <Notice tone="done">
          <p>{t('Your app restarted and picked up your changes.')}</p>
        </Notice>
      ) : null}

      <SectionPanel title={t('Keys')} flush>
        <ul className="keys__list">
          {reserved.map((secret) => (
            <ReservedRow secret={secret} key={secret.id} />
          ))}
          {keys.map((secret) => (
            <KeyRow
              key={secret.id}
              secret={secret}
              pickedUpBy={deployFor(secret)}
              advanced={advanced}
              onReplace={() => {
                // Replace is the add form with the name already in it, which
                // walks straight into the duplicate path: the form explains
                // the consequence and the primary relabels itself. One flow,
                // not two, and the explanation is the one a person needs.
                setName(secret.key);
                setPickedName(secret.key);
                focusKeyName();
              }}
              onDelete={() => setDeleting(secret)}
            />
          ))}
        </ul>

        {/* The empty state's action is the form directly beneath it, so it
            carries no button of its own. A second Add control here would put
            two of the same action on one screen. */}
        {keys.length === 0 ? (
          <div className="keys__empty">
            <EmptyState
              mark="key"
              sentence={t("You haven't added any keys yet.")}
              context={t(
                "You'll need one when your app uses another service, like sending email or taking payments.",
              )}
            />
          </div>
        ) : null}
      </SectionPanel>

      <SectionPanel title={t('Add a key')}>
        {/* autoComplete="off" on the form AND on both fields, plus names that
            are nothing like "username"/"password". A text input followed by
            a type=password input is the exact shape every browser's
            credential heuristic looks for, so Chrome was offering to fill a
            person's own SV Cloud login into somebody's OPENAI_API_KEY and
            then offering to save the key as a password. "new-password" on
            the value is the documented way to tell a manager "this is a
            secret being created, do not offer a stored one". */}
        <form
          className="keys__form"
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            onAdd();
          }}
        >
          <div className="keys__fields">
            <Field
              label={t('Name')}
              value={name}
              onFocus={() => formTracker.onFieldFocus('name')}
              onBlur={() => formTracker.onFieldBlur('name')}
              onChange={(event) => {
                setName(event.target.value);
                formTracker.onFieldChange('name', event.target.value);
              }}
              mono
              name="sv-cloud-key-name"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              id={NAME_FIELD_ID}
              // The duplicate sentence goes in the help line, not the error
              // line. The error line sets aria-invalid, and OPENAI_API_KEY is
              // not an invalid name: it is a name with a consequence, which is
              // a different thing and gets a different tone.
              help={
                takenByPlatform
                  ? t('{name} is managed by SV Cloud, so it cannot be replaced here.', {
                      name: takenByPlatform.key,
                    })
                  : duplicate
                    ? t('A key called {name} already exists. Adding it again replaces the old one.', {
                        name: duplicate.key,
                      })
                    : undefined
              }
            />
            <Field
              label={t('Value')}
              value={value}
              onFocus={() => formTracker.onFieldFocus('value')}
              onBlur={() => formTracker.onFieldBlur('value')}
              onChange={(event) => {
                setValue(event.target.value);
                formTracker.onFieldChange('value', event.target.value);
              }}
              type={valueShown ? 'text' : 'password'}
              // Analytics never reads this field, even when the value is shown.
              className="ph-no-capture"
              mono
              name="sv-cloud-key-value"
              autoComplete="new-password"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              adornment={
                <button
                  type="button"
                  className="keys__eye"
                  aria-pressed={valueShown}
                  aria-label={valueShown ? t('Hide the value') : t('Show the value')}
                  title={valueShown ? t('Hide the value') : t('Show the value')}
                  onClick={() => {
                    captureEvent('secret_value_visibility_toggled', {
                      project_slug: slug,
                      // The value itself is never sent, only the direction of
                      // the toggle. See the analytics privacy policy.
                      shown: !valueShown,
                    });
                    setValueShown((was) => !was);
                  }}
                >
                  <EyeGlyph shown={valueShown} />
                </button>
              }
            />
          </div>

          <div className="keys__chips">
            <span className="keys__chips-label">{t('Common keys')}</span>
            {common.map((item) => (
              <button
                type="button"
                className="keys__chip"
                key={item.name}
                aria-pressed={pickedName === item.name}
                onClick={() => {
                  captureEvent('secret_preset_chip_clicked', { preset_name: item.name });
                  setPickedName(item.name);
                  setName(item.name);
                  formTracker.onFieldChange('name', item.name);
                }}
              >
                {item.chip}
              </button>
            ))}
          </div>

          {picked ? (
            <p className="keys__where">
              <span>{picked.where}</span>
              <a className="link-inline" href={picked.href} target="_blank" rel="noreferrer">
                {t('Where to get it')}
              </a>
            </p>
          ) : null}

          <div className="keys__submit">
            <Button
              variant="solid"
              type="submit"
              disabled={name.trim().length === 0 || value.length === 0 || takenByPlatform !== null}
            >
              {duplicate ? t('Replace key') : t('Add key')}
            </Button>
          </div>
        </form>
      </SectionPanel>

      {/* Appends below everything and never changes a section above. It used
          to be a raw mode check, which put a whole section of the person's own
          configuration out of reach in Simple with no way to even know it was
          there. The disclosure puts it one click away instead.

          "Non-secret settings" is gone with it. The page is called Keys and
          settings, so the section is called Settings, and the note carries the
          distinction in one sentence rather than smuggling it into a heading.
          Even Advanced keeps the product's own words for the product's own
          things. */}
      {settings.length > 0 ? (
        <div className="keys__advanced">
          <AdvancedDisclosure label={t('Show the settings your app can read')}>
            <SectionPanel
              title={t('Settings')}
              note={t('Settings are readable. Keys are not.')}
              flush
            >
              <ul className="keys__list">
                {settings.map((setting) => (
                  <li className="keyrow" key={setting.key}>
                    <div className="keyrow__main">
                      <div className="keyrow__top">
                        <span className="keyrow__name mono">{setting.key}</span>
                        <span className="keyrow__value mono">{setting.value}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </SectionPanel>
          </AdvancedDisclosure>
        </div>
      ) : null}

      {/* Reversible, so no type to confirm. Spending that pattern here is what
          makes people type through the dialog that matters. */}
      <DestructiveConfirm
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) {
            const key = deleting.key;
            const at = Date.now();
            setRemoved((was) => [...was, deleting.id]);
            setChangedAt(at);
            if (!USE_MOCK) markKeysChanged(projectId, at);
            if (!USE_MOCK) {
              deleteSecret(projectId, key).catch(() => {
                showToast(t('{name} was not deleted. Try again.', { name: key }));
              });
            }
          }
          setDeleting(null);
        }}
        title={t('Delete {name}?', { name: deleting?.key ?? '' })}
        objectName={deleting?.key ?? ''}
        confirmLabel={t('Delete this key')}
        consequences={[
          { kind: 'loss', text: t('Your app will stop being able to use it.') },
          { kind: 'survives', text: t('Everything your app has already saved stays as it is.') },
          { kind: 'survives', text: t('You can add it again at any time.') },
        ]}
      />
    </div>
  );
}

function Head() {
  return (
    <header className="page__head">
      <h1 className="page__title">{t('Keys and settings')}</h1>
      <p className="page__lede">
        {t("Passwords and keys your app needs. Your app can read these. We can't.")}
      </p>
    </header>
  );
}
